import React from "react";
import {RefreshCw, Boxes} from "lucide-react";
import {cn} from "../../utils/cn";

/**
 * Compact dashboard header strip — purely chrome.
 *
 * Sits at the top of the dashboard-shell without affecting the
 * underlying 2-column workspace (DeviceList + DeviceDetailsPanel +
 * DashboardInsights). Visual goals only:
 *   - matches the PageHeader idiom used on Users/Assets/Reports
 *   - shows the count of in-scope devices (Fleet overview / Your
 *     assigned assets) at a glance
 *   - exposes a refresh button (geocode + WS reconnect happens via
 *     the existing fetchAndUpdateLocation handler)
 */
export default function DashboardHeader({isEmployee, deviceCount, onRefresh, isRefreshing}) {
  return (
    <header className="shrink-0 px-3 sm:px-5 py-2.5 bg-white border border-ink-200 rounded-xl shadow-xs flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-lg bg-brand-50 grid place-items-center shrink-0">
          <Boxes className="h-4 w-4 text-brand-700" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h1 className="text-base sm:text-lg font-semibold text-ink-900 leading-tight truncate">
            Dashboard
          </h1>
          <p className="text-xs text-ink-500 truncate">
            {isEmployee ? "Your assigned assets" : "Fleet overview"}
            <span className="mx-2 text-ink-300">·</span>
            <span className="font-medium text-ink-700 tabular-nums">{deviceCount}</span>{" "}
            device{deviceCount === 1 ? "" : "s"} in scope
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className={cn(
            "inline-flex items-center gap-2 h-8 px-3 rounded-md",
            "text-sm font-medium border border-ink-200 bg-white text-ink-700",
            "hover:bg-ink-50 hover:border-ink-300",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "focus-visible:outline-none focus-visible:ring"
          )}
          aria-label="Refresh devices"
        >
          <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} aria-hidden="true" />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>
    </header>
  );
}
